/**
 * Campaign Understanding screen (PRD 10.2).
 *
 * The planner's job here is to confirm or correct the interpretation before
 * anything is searched. So the screen shows, for every field: the value, the
 * confidence, and the exact words it was read from - and it will not let the
 * search start while a blocking field is unresolved.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { BriefResponse, Clarification, FieldEvidence } from '../lib/types'
import {
  AFFLUENCE_LABEL,
  OBJECTIVE_LABEL,
  dateRange,
  money,
  percent,
  titleCase,
} from '../lib/format'
import { Callout, EmptyState, ErrorCallout } from '../components/Callout'
import { Chip } from '../components/Chips'
import { Skeleton } from '../components/Metric'
import { AgentPanel } from '../components/AgentPanel'
import { ResizablePanel } from '../components/ResizablePanel'
import { StepIndicator } from '../components/StepIndicator'
import { applyBriefStateEvent } from '../lib/briefState'

const MEDIA_TYPES = ['Billboard', 'Bus Shelter', 'Bus Panel', 'Digital Screen', 'Display Panel', 'BQSP']
const TIERS = ['General', 'Premium', 'Super Premium', 'Ultra Premium']
const OBJECTIVES = ['new_launch', 'awareness', 'consideration', 'footfall', 'retention', 'tactical']
const AFFLUENCE = ['mass', 'mid', 'mid_to_premium', 'premium']

function EvidenceHint({ field, evidence }: { field: string; evidence: FieldEvidence[] }) {
  const hits = evidence.filter((e) => e.field === field)
  if (!hits.length) return null
  return (
    <div className="tiny faint">
      {hits.map((hit, i) => (
        <span key={i}>
          {i > 0 ? ' · ' : ''}
          {hit.source_span ? <>read from “{hit.source_span}”</> : 'no source span'}
          {' '}
          <span title="Extraction confidence">({percent(hit.confidence)})</span>
          {hit.origin === 'derived' ? (
            <span title="Inferred from a planning convention, not stated in your text">
              {' '}
              · derived
            </span>
          ) : null}
        </span>
      ))}
    </div>
  )
}

function ClarificationCard({
  clarification,
  onSuggest,
  onFlexible,
  busy,
}: {
  clarification: Clarification
  //: Fills the Planning assistant's message box with this suggestion - it
  //: isn't answered here. The assistant is the one path that reliably
  //: interprets a free-text answer (typos, unusual phrasing, unit
  //: conversions like "20 cr") rather than a rigid client-side re-parse.
  onSuggest: (text: string) => void
  onFlexible: (field: string) => void
  busy: boolean
}) {
  const blocking = clarification.severity === 'blocking'
  return (
    <div className={`callout ${blocking ? 'warn' : 'info'}`}>
      <span className="icon" aria-hidden>
        {blocking ? '!' : '?'}
      </span>
      <div className="body stack tight">
        <div>
          <strong>{clarification.question}</strong>
          {clarification.why ? <div className="tiny">{clarification.why}</div> : null}
        </div>
        {clarification.suggestions.length ? (
          <div className="pill-row">
            {clarification.suggestions.map((suggestion) => (
              <button
                key={suggestion}
                className="small"
                disabled={busy}
                title="Fill this into the Planning assistant's message box"
                onClick={() => onSuggest(suggestion)}
              >
                {titleCase(suggestion)}
              </button>
            ))}
          </div>
        ) : null}
        <div className="row">
          <span className="tiny faint">Answer in the Planning assistant →</span>
          {blocking ? (
            <button
              className="small ghost"
              disabled={busy}
              onClick={() => onFlexible(clarification.field)}
              title="Let the planner decide this for you, and show the assumption it made"
            >
              Decide for me
            </button>
          ) : (
            <button
              className="small ghost"
              disabled={busy}
              onClick={() => onSuggest(`Skip this one - "${clarification.question}"`)}
              title="Tell the assistant to skip this and move on"
            >
              Skip
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export function BriefWorkspace() {
  const { briefId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [llm, setLlm] = useState<{ available: boolean; model: string | null }>({
    available: false,
    model: null,
  })
  const [prefill, setPrefill] = useState<{ text: string; token: number } | null>(null)

  const brief = useQuery<BriefResponse>({
    queryKey: ['brief', briefId],
    queryFn: () => api.getBrief(briefId!),
    enabled: Boolean(briefId),
  })

  // One planning session per brief, so the assistant keeps its context.
  useEffect(() => {
    if (!briefId || sessionId) return
    api
      .createSession(briefId)
      .then((session) => {
        setSessionId(session.session_id)
        setLlm({ available: session.llm_available, model: session.llm_model })
      })
      .catch(() => setSessionId(null))
  }, [briefId, sessionId])

  const update = useMutation({
    mutationFn: (payload: { changes: Record<string, unknown>; reason?: string }) =>
      api.updateBrief(briefId!, payload.changes, payload.reason),
    onSuccess: (result) => queryClient.setQueryData(['brief', briefId], result),
  })

  const flexible = useMutation({
    mutationFn: (fields: string[]) => api.markFlexible(briefId!, fields),
    onSuccess: (result) => queryClient.setQueryData(['brief', briefId], result),
  })

  if (!briefId) {
    return (
      <main className="main">
        <div className="page">
          <EmptyState
            title="No brief selected"
            action={
              <button className="primary" onClick={() => navigate('/')}>
                Start a brief
              </button>
            }
          />
        </div>
      </main>
    )
  }

  if (brief.isLoading) {
    return (
      <main className="main">
        <div className="page">
          <Skeleton rows={6} />
        </div>
      </main>
    )
  }
  if (brief.isError || !brief.data) {
    return (
      <main className="main">
        <div className="page">
          <ErrorCallout error={brief.error} onRetry={() => void brief.refetch()} />
        </div>
      </main>
    )
  }

  const data = brief.data
  const document = data.brief
  const validation = data.validation
  const clarifications = validation?.clarifications ?? data.clarifications
  const blocking = clarifications.filter((c) => c.severity === 'blocking')
  const errors = validation?.issues.filter((i) => i.severity === 'error') ?? []
  const warnings = validation?.issues.filter((i) => i.severity === 'warning') ?? []
  const busy = update.isPending || flexible.isPending

  // A clarification suggestion fills the Planning assistant's message box
  // rather than being answered here - see ClarificationCard. `token` forces
  // AgentPanel's effect to re-fire even on a repeat click of the same text.
  const suggest = (text: string) => setPrefill({ text, token: Date.now() })

  // The top outstanding clarification (blocking first) - handed to the chat
  // panel so it can open the Q&A itself instead of waiting for the planner
  // to speak first.
  const severityRank: Record<string, number> = { blocking: 0, high_impact: 1, optional: 2 }
  const topClarification = [...clarifications].sort(
    (a, b) => (severityRank[a.severity] ?? 3) - (severityRank[b.severity] ?? 3),
  )[0]

  return (
    <main className="main full">
      <div className="workspace">
        <div className="workspace-main">
          <div>
            <div style={{ padding: '8px 14px 10px' }}>
              <StepIndicator step={1} />
            </div>
            <div className="toolbar">
              <div className="grow">
                <h1 style={{ fontSize: 16 }}>
                  {document.brand ?? 'Campaign'} brief
                  <span className="faint" style={{ fontWeight: 400 }}>
                    {' '}
                    v{data.version}
                  </span>
                </h1>
                <div className="tiny faint">
                  {data.extraction_mode === 'deterministic'
                    ? 'Parsed with the deterministic extractor'
                    : 'Parsed with the language model, then validated against the schema'}
                  {data.tokens_used > 0 && ` · ${data.tokens_used.toLocaleString()} tokens used`}
                </div>
              </div>
              {validation?.can_search ? (
                <Chip tone="ok">Ready to search</Chip>
              ) : (
                <Chip tone="warn">Needs {blocking.length || errors.length} answer(s)</Chip>
              )}
              <button
                className="primary"
                disabled={!validation?.can_search}
                onClick={() => navigate(`/recommendations/${briefId}`)}
                title={
                  validation?.can_search
                    ? 'Run eligibility, scoring and optimization'
                    : 'Resolve the blocking questions first'
                }
              >
                Recommend assets
              </button>
            </div>
          </div>

          <div className="scroll-y" style={{ padding: 14 }}>
            <div className="stack">
              {errors.map((issue) => (
                <Callout key={issue.code} tone="danger" title={issue.message}>
                  {issue.guidance}
                </Callout>
              ))}

              {clarifications.map((clarification) => (
                <ClarificationCard
                  key={clarification.field}
                  clarification={clarification}
                  onSuggest={suggest}
                  onFlexible={(field) => flexible.mutate([field])}
                  busy={busy}
                />
              ))}

              {warnings.map((issue) => (
                <Callout key={issue.code} tone="warn" title={issue.message}>
                  {issue.guidance}
                </Callout>
              ))}

              {update.isError ? <ErrorCallout error={update.error} /> : null}

              <div className="card">
                <div className="card-head">
                  <h2 className="grow">What the system understood</h2>
                  <span className="tiny faint">Edit any field; each edit creates a version</span>
                </div>
                <div className="card-body grid two">
                  <div className="stack tight">
                    <label className="field">
                      Brand
                      <input
                        key={`brand-${data.version}`}
                        defaultValue={document.brand ?? ''}
                        onBlur={(e) =>
                          e.target.value !== (document.brand ?? '') &&
                          update.mutate({ changes: { brand: e.target.value || null } })
                        }
                      />
                    </label>
                    <EvidenceHint field="brand" evidence={data.field_evidence} />
                  </div>

                  <div className="stack tight">
                    <label className="field">
                      Objective
                      <select
                        value={document.objective}
                        onChange={(e) => update.mutate({ changes: { objective: e.target.value } })}
                      >
                        <option value="unspecified">Not specified</option>
                        {OBJECTIVES.map((objective) => (
                          <option key={objective} value={objective}>
                            {OBJECTIVE_LABEL[objective]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <EvidenceHint field="objective" evidence={data.field_evidence} />
                  </div>

                  <div className="stack tight">
                    <h4>Geography</h4>
                    <div className="pill-row">
                      {document.geographies.length ? (
                        document.geographies.map((geo) => (
                          <span key={`${geo.type}-${geo.name}`} className="chip neutral">
                            {geo.name} · {geo.type}
                          </span>
                        ))
                      ) : (
                        <span className="chip unavailable">Not given</span>
                      )}
                    </div>
                    {validation?.matched_cities.length ? (
                      <div className="tiny faint">
                        Matched in Captura: {validation.matched_cities.map((c) => c.name).join(', ')}
                      </div>
                    ) : null}
                    <EvidenceHint field="geographies" evidence={data.field_evidence} />
                  </div>

                  <div className="stack tight">
                    <h4>Campaign window</h4>
                    <div className="row">
                      <label className="field grow">
                        Start
                        <input
                          key={`start_date-${data.version}`}
                          type="date"
                          defaultValue={document.dates.start_date ?? ''}
                          onChange={(e) =>
                            update.mutate({
                              changes: { dates: { ...document.dates, start_date: e.target.value } },
                            })
                          }
                        />
                      </label>
                      <label className="field grow">
                        Days
                        <input
                          key={`duration_days-${data.version}`}
                          type="number"
                          min={1}
                          defaultValue={document.dates.duration_days ?? ''}
                          onBlur={(e) =>
                            e.target.value &&
                            update.mutate({
                              changes: {
                                dates: {
                                  ...document.dates,
                                  duration_days: Number(e.target.value),
                                  end_date: null,
                                },
                              },
                            })
                          }
                        />
                      </label>
                    </div>
                    <div className="tiny muted">
                      {validation?.resolved_dates
                        ? dateRange(
                          validation.resolved_dates.start_date,
                          validation.resolved_dates.end_date,
                        )
                        : dateRange(document.dates.start_date, document.dates.end_date)}
                    </div>
                    <EvidenceHint field="dates" evidence={data.field_evidence} />
                  </div>

                  <div className="stack tight">
                    <label className="field">
                      Budget (₹)
                      <input
                        key={`budget-${data.version}`}
                        type="number"
                        min={0}
                        step={10000}
                        defaultValue={
                          document.budget ? document.budget.amount_minor / 100 : ''
                        }
                        onBlur={(e) =>
                          e.target.value &&
                          update.mutate({
                            changes: {
                              budget: {
                                amount_minor: Math.round(Number(e.target.value) * 100),
                                currency: 'INR',
                              },
                            },
                          })
                        }
                      />
                    </label>
                    <div className="tiny muted">
                      {document.budget
                        ? money(document.budget.amount_minor, document.budget.currency)
                        : 'Not given'}
                    </div>
                    <EvidenceHint field="budget" evidence={data.field_evidence} />
                  </div>

                  <div className="stack tight">
                    <h4>Audience</h4>
                    <div className="row">
                      <label className="field grow">
                        Age from
                        <input
                          key={`age_min-${data.version}`}
                          type="number"
                          min={0}
                          max={120}
                          defaultValue={document.audience.age_min ?? ''}
                          onBlur={(e) =>
                            update.mutate({
                              changes: {
                                audience: {
                                  ...document.audience,
                                  age_min: e.target.value ? Number(e.target.value) : null,
                                },
                              },
                            })
                          }
                        />
                      </label>
                      <label className="field grow">
                        to
                        <input
                          key={`age_max-${data.version}`}
                          type="number"
                          min={0}
                          max={120}
                          defaultValue={document.audience.age_max ?? ''}
                          onBlur={(e) =>
                            update.mutate({
                              changes: {
                                audience: {
                                  ...document.audience,
                                  age_max: e.target.value ? Number(e.target.value) : null,
                                },
                              },
                            })
                          }
                        />
                      </label>
                    </div>
                    <label className="field">
                      Affluence
                      <select
                        value={document.audience.affluence}
                        onChange={(e) =>
                          update.mutate({
                            changes: {
                              audience: { ...document.audience, affluence: e.target.value },
                            },
                          })
                        }
                      >
                        <option value="unspecified">Not specified</option>
                        {AFFLUENCE.map((band) => (
                          <option key={band} value={band}>
                            {AFFLUENCE_LABEL[band]}
                          </option>
                        ))}
                      </select>
                    </label>
                    {document.audience.interests.length ? (
                      <div className="pill-row">
                        {document.audience.interests.map((interest) => (
                          <span key={interest} className="chip neutral">
                            {interest}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    <EvidenceHint field="audience" evidence={data.field_evidence} />
                  </div>

                  <div className="stack tight">
                    <h4>Formats and tiers</h4>
                    <div className="pill-row">
                      {MEDIA_TYPES.map((type) => {
                        const on = document.formats.media_types.includes(type)
                        return (
                          <button
                            key={type}
                            className={`small ${on ? 'primary' : ''}`}
                            onClick={() =>
                              update.mutate({
                                changes: {
                                  formats: {
                                    ...document.formats,
                                    media_types: on
                                      ? document.formats.media_types.filter((t) => t !== type)
                                      : [...document.formats.media_types, type],
                                  },
                                },
                              })
                            }
                          >
                            {type}
                          </button>
                        )
                      })}
                    </div>
                    <div className="pill-row">
                      {TIERS.map((tier) => {
                        const on = document.formats.strategy_tiers.includes(tier)
                        return (
                          <button
                            key={tier}
                            className={`small ${on ? 'primary' : ''}`}
                            onClick={() =>
                              update.mutate({
                                changes: {
                                  formats: {
                                    ...document.formats,
                                    strategy_tiers: on
                                      ? document.formats.strategy_tiers.filter((t) => t !== tier)
                                      : [...document.formats.strategy_tiers, tier],
                                  },
                                },
                              })
                            }
                          >
                            {tier}
                          </button>
                        )
                      })}
                    </div>
                    <label className="row" style={{ gap: 6 }}>
                      <input
                        type="checkbox"
                        style={{ width: 'auto' }}
                        checked={document.formats.illuminated_only}
                        onChange={(e) =>
                          update.mutate({
                            changes: {
                              formats: {
                                ...document.formats,
                                illuminated_only: e.target.checked,
                              },
                            },
                          })
                        }
                      />
                      <span className="small">Illuminated sites only</span>
                    </label>
                    <div className="row">
                      <label className="field grow">
                        Min assets
                        <input
                          key={`min_asset_count-${data.version}`}
                          type="number"
                          min={1}
                          defaultValue={document.formats.min_asset_count ?? ''}
                          onBlur={(e) =>
                            update.mutate({
                              changes: {
                                formats: {
                                  ...document.formats,
                                  min_asset_count: e.target.value ? Number(e.target.value) : null,
                                },
                              },
                            })
                          }
                        />
                      </label>
                      <label className="field grow">
                        Max assets
                        <input
                          key={`max_asset_count-${data.version}`}
                          type="number"
                          min={1}
                          defaultValue={document.formats.max_asset_count ?? ''}
                          onBlur={(e) =>
                            update.mutate({
                              changes: {
                                formats: {
                                  ...document.formats,
                                  max_asset_count: e.target.value ? Number(e.target.value) : null,
                                },
                              },
                            })
                          }
                        />
                      </label>
                    </div>
                  </div>

                  <div className="stack tight">
                    <h4>Priority points of interest</h4>
                    {document.poi_preferences.length ? (
                      <div className="pill-row">
                        {document.poi_preferences.map((poi) => (
                          <span key={poi.category} className="chip neutral">
                            {titleCase(poi.category)} · {percent(poi.importance)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="tiny faint">
                        None named, so campaign POI relevance is reported as unavailable rather
                        than guessed.
                      </div>
                    )}
                    <EvidenceHint field="poi_preferences" evidence={data.field_evidence} />
                  </div>
                </div>
              </div>

              {data.unmapped_mentions.length ? (
                <Callout tone="info" title="Noticed but not mapped to a field">
                  {data.unmapped_mentions.join(' · ')}
                </Callout>
              ) : null}

              <details className="disclosure">
                <summary>Original brief text</summary>
                <div className="small mono" style={{ whiteSpace: 'pre-wrap' }}>
                  {data.original_text}
                </div>
              </details>
            </div>
          </div>
        </div>

        <ResizablePanel storageKey="agentPanel" title="Planning assistant">
          <AgentPanel
            sessionId={sessionId}
            llmAvailable={llm.available}
            llmModel={llm.model}
            onStateChange={(data) => applyBriefStateEvent(queryClient, briefId!, data)}
            onRunCreated={() => navigate(`/recommendations/${briefId}`)}
            prefill={prefill}
            autoKickoff={
              topClarification
                ? { question: topClarification.question, why: topClarification.why }
                : null
            }
          />
        </ResizablePanel>
      </div>
    </main>
  )
}

